import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { 
    ArrowLeft, Download, Loader2, Calendar, Activity, AlertTriangle, 
    Target, Clock, CheckCircle, Brain, Eye, UserX, UserCheck, Trash2,
    MessageSquare, FileText, Star, Shield, ChevronDown, ChevronUp, Send,
    KeyRound, Copy, Check
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { formatDate, formatDateTime } from '../lib/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_NAMES = {
    diferencial_inicial: 'Diferencial inicial', registro_episodios: 'Registro episodios',
    semaforo: 'Semáforo conductual', doble_capa: 'Doble capa', linea_base: 'Línea base',
    mapa_amenaza: 'Mapa amenaza', radar_exigencia: 'Radar exigencia',
    contrato_oculto: 'Contrato oculto', arbol_temor: 'Árbol temor', radiografia: 'Radiografía',
    inventario_frentes: 'Inventario frentes', deber_prestado: 'Deber prestado',
    filtro_impacto: 'Filtro impacto', tarea_faro_3: 'Tarea faro', dai: 'DAI',
    descomposicion_momento: 'Descomposición momento', microsenales: 'Microseñales',
    reescritura: 'Reescritura', intervencion_vivo: 'Intervención en vivo', automatizacion: 'Automatización',
    detector_friccion: 'Detector de fricción', vmv: 'Versión mínima viable',
    diseno_entorno: 'Diseño de entorno', arranque_obligatorio: 'Arranque obligatorio',
    sistema_ejecucion: 'Sistema de ejecución',
    registro_accion: 'Registro de acción visible', cadena_ejecucion: 'Cadena de ejecución',
    escala_ajuste: 'Escala de ajuste', metricas_funcionales: 'Métricas funcionales',
    ciclo_progreso: 'Ciclo de progreso',
    evaluacion_funcional: 'Evaluación funcional', mapa_patrones: 'Mapa de patrones',
    punto_intervencion: 'Punto de intervención', rediseno_patron: 'Rediseño de patrón',
    mapa_decision: 'Mapa de decisión',
    diagnostico_sostenibilidad: 'Revisión de sostenibilidad', reinicio_rapido: 'Reinicio rápido',
    proteccion_recaidas: 'Protección contra recaídas', refuerzo_inteligente: 'Refuerzo inteligente',
    sistema_autonomo: 'Sistema autónomo',
    senales_integracion: 'Señales de integración', transferencia_sistema: 'Transferencia del sistema',
    criterio_propio: 'Criterio propio', identidad_operativa: 'Identidad operativa',
    vision_operativa: 'Visión operativa',
};

const CHAPTER_NAMES = { 1: 'Autoexploración', 2: 'Amenaza y Defensa', 3: 'Foco y Acción', 4: 'Intervención y Automatización', 5: 'Ejecución sin Fricción', 6: 'Sostenimiento y Progreso', 7: 'Evaluación y Rediseño', 8: 'Revisión y Sistema Autónomo', 9: 'Integración y Cierre' };
const MODULE_CHAPTER = {
    diferencial_inicial: 1, registro_episodios: 1, semaforo: 1, doble_capa: 1, linea_base: 1,
    mapa_amenaza: 2, radar_exigencia: 2, contrato_oculto: 2, arbol_temor: 2, radiografia: 2,
    inventario_frentes: 3, deber_prestado: 3, filtro_impacto: 3, tarea_faro_3: 3, dai: 3,
    descomposicion_momento: 4, microsenales: 4, reescritura: 4, intervencion_vivo: 4, automatizacion: 4,
    detector_friccion: 5, vmv: 5, diseno_entorno: 5, arranque_obligatorio: 5, sistema_ejecucion: 5,
    registro_accion: 6, cadena_ejecucion: 6, escala_ajuste: 6, metricas_funcionales: 6, ciclo_progreso: 6,
    evaluacion_funcional: 7, mapa_patrones: 7, punto_intervencion: 7, rediseno_patron: 7, mapa_decision: 7,
    diagnostico_sostenibilidad: 8, reinicio_rapido: 8, proteccion_recaidas: 8, refuerzo_inteligente: 8, sistema_autonomo: 8,
    senales_integracion: 9, transferencia_sistema: 9, criterio_propio: 9, identidad_operativa: 9, vision_operativa: 9,
};

const SECTIONS = [
    { id: 'resumen', label: 'Resumen' },
    { id: 'modulos', label: 'Módulos' },
    { id: 'observaciones', label: 'Observaciones' },
    { id: 'informes', label: 'Informes' },
    { id: 'feedback', label: 'Feedback' },
    { id: 'auditoria', label: 'Auditoría' },
];

export default function PerfilUsuario() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('resumen');
    // Observation state
    const [obsText, setObsText] = useState('');
    const [obsModule, setObsModule] = useState('');
    const [obsChapter, setObsChapter] = useState('');
    const [submittingObs, setSubmittingObs] = useState(false);
    // Report editing
    const [editingReport, setEditingReport] = useState(null);
    const [editedSummary, setEditedSummary] = useState('');
    const [editedRecommendation, setEditedRecommendation] = useState('');
    // Module detail expansion
    const [expandedModule, setExpandedModule] = useState(null);
    // Password reset
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [tempPassword, setTempPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const fetchProfile = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/mentor/user/${userId}`, { withCredentials: true });
            setProfile(data);
        } catch { toast.error('Error al cargar perfil'); navigate('/mentor'); }
        finally { setLoading(false); }
    }, [userId, navigate]);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    const handleDownloadPDF = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/mentor/user/${userId}/pdf`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', `SENDA_Informe_${profile?.name?.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
            toast.success('PDF descargado');
        } catch { toast.error('Error al descargar PDF'); }
    };

    const handleSuspend = async () => {
        try { await axios.post(`${API_URL}/api/mentor/user/${userId}/suspend`, {}, { withCredentials: true }); toast.success('Usuario suspendido'); fetchProfile(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };
    const handleReactivate = async () => {
        try { await axios.post(`${API_URL}/api/mentor/user/${userId}/reactivate`, {}, { withCredentials: true }); toast.success('Usuario reactivado'); fetchProfile(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };
    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar este usuario permanentemente?')) return;
        try { await axios.post(`${API_URL}/api/mentor/user/${userId}/delete`, {}, { withCredentials: true }); toast.success('Usuario eliminado'); navigate('/mentor'); }
        catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
    };

    const handleAddObservation = async () => {
        if (!obsText.trim()) return;
        setSubmittingObs(true);
        try {
            await axios.post(`${API_URL}/api/mentor/observation`, {
                user_id: userId, text: obsText,
                module_id: obsModule || null,
                chapter: obsChapter ? parseInt(obsChapter) : null
            }, { withCredentials: true });
            toast.success('Observación agregada');
            setObsText(''); setObsModule(''); setObsChapter('');
            fetchProfile();
        } catch { toast.error('Error al agregar observación'); }
        finally { setSubmittingObs(false); }
    };

    const handleApproveReport = async (reportId, sendToUser) => {
        try {
            await axios.post(`${API_URL}/api/mentor/report/${reportId}/approve`, { send_to_user: sendToUser }, { withCredentials: true });
            toast.success(sendToUser ? 'Informe aprobado y enviado' : 'Informe aprobado');
            setEditingReport(null);
            fetchProfile();
        } catch { toast.error('Error al aprobar informe'); }
    };

    const handleEditReport = async (reportId) => {
        try {
            await axios.put(`${API_URL}/api/mentor/report/${reportId}/edit`, {
                edited_content: { resumen: editedSummary, recomendacion: editedRecommendation }
            }, { withCredentials: true });
            toast.success('Informe editado');
            fetchProfile();
        } catch { toast.error('Error al editar'); }
    };

    const handleResetPassword = async () => {
        setResetLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/mentor/user/${userId}/reset-password`,
                {},
                { withCredentials: true }
            );
            setTempPassword(data.temp_password);
            setCopied(false);
            toast.success('Contraseña reseteada exitosamente');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al resetear contraseña');
            setResetPasswordOpen(false);
        } finally {
            setResetLoading(false);
        }
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(tempPassword);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    if (loading) return <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;
    if (!profile) return null;

    const completedModules = profile.modules_completed || [];
    const observations = profile.observations || [];
    const reports = profile.reports || [];
    const feedbacks = profile.feedbacks || [];
    const auditLogs = profile.audit_logs || [];

    return (
        <div className="min-h-screen bg-[#FBFBF9]">
            <Toaster position="top-right" richColors />
            
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                            <Link to="/mentor"><Button variant="ghost" size="icon" className="text-[#3E5F6E] shrink-0"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit'] truncate" data-testid="user-detail-name">{profile.name}</h1>
                                    <Badge className={profile.status === 'activo' ? 'bg-[#5FAF9D]/10 text-[#5FAF9D]' : profile.status === 'suspendido' ? 'bg-[#E8B27A]/10 text-[#E8B27A]' : 'bg-[#D07F7E]/10 text-[#D07F7E]'}>{profile.status}</Badge>
                                </div>
                                <p className="text-xs text-[#9AA5B1] truncate">{profile.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                            <Button variant="outline" size="sm" className="border-[#EAE8E3] text-[#3E5F6E]" onClick={handleDownloadPDF} data-testid="download-user-pdf"><Download className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">PDF</span></Button>
                            <Button variant="outline" size="sm" className="border-[#A29DB0]/30 text-[#A29DB0]" onClick={() => { setTempPassword(''); setResetPasswordOpen(true); }} data-testid="reset-password-btn"><KeyRound className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Resetear contraseña</span></Button>
                            {profile.status === 'activo' && <Button variant="outline" size="sm" className="border-[#E8B27A]/30 text-[#E8B27A]" onClick={handleSuspend} data-testid="suspend-user-btn"><UserX className="h-4 w-4" /></Button>}
                            {profile.status === 'suspendido' && <Button variant="outline" size="sm" className="border-[#5FAF9D]/30 text-[#5FAF9D]" onClick={handleReactivate} data-testid="reactivate-user-btn"><UserCheck className="h-4 w-4" /></Button>}
                            {!profile.is_deleted && <Button variant="outline" size="sm" className="border-[#D07F7E]/30 text-[#D07F7E]" onClick={handleDelete} data-testid="delete-user-btn"><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </div>
                    </div>
                </div>
            </header>

            {/* Section Tabs */}
            <div className="bg-white border-b border-[#EAE8E3]/50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <nav className="flex gap-1 overflow-x-auto" data-testid="user-detail-tabs">
                        {SECTIONS.map(s => (
                            <button key={s.id} onClick={() => setActiveSection(s.id)}
                                className={`px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-all ${activeSection === s.id ? 'border-[#3E5F6E] text-[#3E5F6E]' : 'border-transparent text-[#9AA5B1] hover:text-[#616E7C]'}`}
                                data-testid={`section-${s.id}`}>{s.label}
                                {s.id === 'informes' && reports.filter(r => r.status === 'borrador').length > 0 && <Badge className="ml-1 bg-[#E8B27A]/10 text-[#E8B27A] text-[10px] px-1">{reports.filter(r => r.status === 'borrador').length}</Badge>}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* ===== RESUMEN ===== */}
                {activeSection === 'resumen' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-4">
                            {/* Key info */}
                            <Card className="bg-white border-[#EAE8E3] shadow-sm">
                                <CardContent className="pt-5">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                        {[
                                            { label: 'Progreso', value: `${profile.completion_percentage || 0}%`, icon: Target, color: '#5FAF9D' },
                                            { label: 'Módulo actual', value: MODULE_NAMES[profile.current_module] || profile.current_module, icon: Activity, color: '#3E5F6E' },
                                            { label: 'Riesgo', value: profile.risk_level || 'bajo', icon: AlertTriangle, color: profile.risk_level === 'alto' ? '#D07F7E' : '#5FAF9D' },
                                            { label: 'Módulos', value: `${completedModules.length}/20`, icon: CheckCircle, color: '#3E5F6E' },
                                        ].map(item => (
                                            <div key={item.label} className="p-3 bg-[#FBFBF9] rounded-xl">
                                                <div className="flex items-center gap-1.5 mb-1"><item.icon className="h-3.5 w-3.5" style={{ color: item.color }} /><p className="text-xs text-[#9AA5B1]">{item.label}</p></div>
                                                <p className="text-sm font-semibold text-[#1F2933] capitalize truncate">{item.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                            {/* Dates */}
                            <Card className="bg-white border-[#EAE8E3] shadow-sm">
                                <CardContent className="pt-5">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        <div><p className="text-xs text-[#9AA5B1]">Fecha creación</p><p className="text-sm text-[#1F2933]">{profile.created_at ? formatDate(profile.created_at) : '-'}</p></div>
                                        <div><p className="text-xs text-[#9AA5B1]">Último login</p><p className="text-sm text-[#1F2933]">{profile.last_login ? formatDateTime(profile.last_login) : 'Nunca'}</p></div>
                                        <div><p className="text-xs text-[#9AA5B1]">Última actividad</p><p className="text-sm text-[#1F2933]">{profile.last_activity ? formatDate(profile.last_activity) : '-'}</p></div>
                                    </div>
                                </CardContent>
                            </Card>
                            {/* AI Summaries */}
                            {profile.ai_summaries?.length > 0 && (
                                <Card className="bg-white border-[#EAE8E3] shadow-sm">
                                    <CardHeader className="pb-2"><CardTitle className="text-sm text-[#1F2933] flex items-center gap-2"><Brain className="h-4 w-4 text-[#5FAF9D]" />Lecturas de avance ({profile.ai_summaries.length})</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="space-y-2 max-h-60 overflow-y-auto">
                                            {profile.ai_summaries.map((s) => (
                                                <div key={`ai-${s.module}`} className="p-2 bg-[#FBFBF9] rounded-lg text-xs">
                                                    <span className="font-semibold text-[#3E5F6E]">{MODULE_NAMES[s.module] || s.module}:</span>
                                                    <span className="text-[#616E7C] ml-1">{s.insight_usuario || s.resumen || 'Sin detalle'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                        {/* Progress sidebar */}
                        <div>
                            <Card className="bg-white border-[#EAE8E3] shadow-sm">
                                <CardHeader className="pb-2"><CardTitle className="text-sm text-[#1F2933]">Progreso por capítulo</CardTitle></CardHeader>
                                <CardContent>
                                    {[1, 2, 3, 4].map(ch => {
                                        const chModules = Object.entries(MODULE_CHAPTER).filter(([_, c]) => c === ch).map(([m]) => m);
                                        const done = chModules.filter(m => completedModules.some(cm => cm.module_id === m)).length;
                                        const pct = (done / chModules.length) * 100;
                                        return (
                                            <div key={ch} className="mb-3">
                                                <div className="flex items-center justify-between mb-1">
                                                    <p className="text-xs font-medium text-[#425A65]">Cap. {ch}: {CHAPTER_NAMES[ch]}</p>
                                                    <span className="text-xs text-[#9AA5B1]">{done}/{chModules.length}</span>
                                                </div>
                                                <div className="h-2 bg-[#EAE8E3] rounded-full overflow-hidden"><div className="h-full bg-[#5FAF9D] transition-all" style={{ width: `${pct}%` }} /></div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                )}

                {/* ===== MÓDULOS ===== */}
                {activeSection === 'modulos' && (
                    <div className="space-y-4">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(ch => {
                            const chModules = Object.entries(MODULE_CHAPTER).filter(([_, c]) => c === ch).map(([m]) => m);
                            return (
                                <Card key={ch} className="bg-white border-[#EAE8E3] shadow-sm">
                                    <CardHeader className="pb-2"><CardTitle className="text-sm text-[#1F2933]">Cap. {ch}: {CHAPTER_NAMES[ch]}</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="space-y-2">
                                            {chModules.map(modId => {
                                                const done = completedModules.find(cm => cm.module_id === modId);
                                                const sub = profile.submissions?.find(s => s.module_id === modId);
                                                const isExpanded = expandedModule === modId;
                                                return (
                                                    <div key={modId} data-testid={`module-detail-${modId}`}>
                                                        <div 
                                                            className={`flex items-center gap-3 p-2 rounded-lg hover:bg-[#FBFBF9] ${sub ? 'cursor-pointer' : ''}`}
                                                            onClick={() => sub && setExpandedModule(isExpanded ? null : modId)}
                                                        >
                                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${done ? 'bg-[#5FAF9D]' : 'bg-[#EAE8E3]'}`}>
                                                                {done ? <CheckCircle className="h-4 w-4 text-white" /> : <Clock className="h-3.5 w-3.5 text-[#9AA5B1]" />}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm text-[#1F2933]">{MODULE_NAMES[modId]}</p>
                                                                {done && <p className="text-xs text-[#9AA5B1]">Completado: {done.completed_at ? formatDate(done.completed_at) : '-'}</p>}
                                                            </div>
                                                            {sub?.ai_analysis && <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-[10px]">Revisado</Badge>}
                                                            {sub && (isExpanded ? <ChevronUp className="h-4 w-4 text-[#9AA5B1]" /> : <ChevronDown className="h-4 w-4 text-[#9AA5B1]" />)}
                                                        </div>
                                                        {isExpanded && sub && (
                                                            <div className="ml-10 mt-2 mb-3 space-y-3" data-testid={`module-expanded-${modId}`}>
                                                                {sub.raw_data && Object.keys(sub.raw_data).length > 0 && (
                                                                    <div className="p-3 bg-[#FBFBF9] rounded-lg border border-[#EAE8E3]">
                                                                        <p className="text-xs font-semibold text-[#425A65] uppercase mb-2">Respuestas del usuario</p>
                                                                        <div className="space-y-2">
                                                                            {Object.entries(sub.raw_data).map(([key, value]) => (
                                                                                <div key={key} className="text-xs">
                                                                                    <span className="font-medium text-[#616E7C]">{key.replace(/_/g, ' ')}:</span>{' '}
                                                                                    <span className="text-[#1F2933]">
                                                                                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {sub.ai_analysis && (
                                                                    <div className="p-3 bg-[#A29DB0]/5 rounded-lg border border-[#A29DB0]/15">
                                                                        <p className="text-xs font-semibold text-[#A29DB0] uppercase mb-2">Lectura de avance</p>
                                                                        <div className="space-y-1">
                                                                            {Object.entries(sub.ai_analysis).map(([key, value]) => (
                                                                                <div key={key} className="text-xs">
                                                                                    <span className="font-medium text-[#616E7C]">{key.replace(/_/g, ' ')}:</span>{' '}
                                                                                    <span className="text-[#1F2933]">
                                                                                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* ===== OBSERVACIONES ===== */}
                {activeSection === 'observaciones' && (
                    <div className="space-y-6">
                        <Card className="bg-white border-[#EAE8E3] shadow-sm">
                            <CardHeader><CardTitle className="text-sm text-[#1F2933] flex items-center gap-2"><MessageSquare className="h-4 w-4 text-[#3E5F6E]" />Agregar observación</CardTitle></CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <select value={obsModule} onChange={e => setObsModule(e.target.value)} className="px-3 py-2 text-sm border border-[#EAE8E3] rounded-lg bg-white text-[#616E7C]" data-testid="obs-module-select">
                                        <option value="">Módulo (opcional)</option>
                                        {Object.entries(MODULE_NAMES).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                                    </select>
                                    <select value={obsChapter} onChange={e => setObsChapter(e.target.value)} className="px-3 py-2 text-sm border border-[#EAE8E3] rounded-lg bg-white text-[#616E7C]" data-testid="obs-chapter-select">
                                        <option value="">Capítulo (opcional)</option>
                                        {Object.entries(CHAPTER_NAMES).map(([num, name]) => <option key={num} value={num}>Cap. {num}: {name}</option>)}
                                    </select>
                                </div>
                                <Textarea value={obsText} onChange={e => setObsText(e.target.value)} placeholder="Escribe tu observación..." className="border-[#EAE8E3] min-h-[80px]" data-testid="obs-text-input" />
                                <Button onClick={handleAddObservation} disabled={!obsText.trim() || submittingObs} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white" data-testid="add-obs-btn">
                                    {submittingObs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Agregar observación
                                </Button>
                            </CardContent>
                        </Card>

                        <div className="space-y-3">
                            {observations.length === 0 ? (
                                <Card className="bg-white border-[#EAE8E3]"><CardContent className="py-8 text-center"><MessageSquare className="h-10 w-10 mx-auto text-[#EAE8E3] mb-2" /><p className="text-sm text-[#9AA5B1]">Sin observaciones</p></CardContent></Card>
                            ) : observations.map((obs) => (
                                <Card key={obs.id || obs.created_at} className="bg-white border-[#EAE8E3] shadow-sm" data-testid={`observation-${obs.id || obs.created_at}`}>
                                    <CardContent className="py-3">
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 bg-[#3E5F6E]/10 rounded-lg flex items-center justify-center shrink-0 mt-0.5"><MessageSquare className="h-4 w-4 text-[#3E5F6E]" /></div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                    <span className="text-xs font-medium text-[#3E5F6E]">{obs.mentor_name || 'Mentora'}</span>
                                                    {obs.module_id && <Badge className="bg-[#3E5F6E]/10 text-[#3E5F6E] text-[10px]">{MODULE_NAMES[obs.module_id] || obs.module_id}</Badge>}
                                                    {obs.chapter && <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-[10px]">Cap. {obs.chapter}</Badge>}
                                                    <span className="text-[10px] text-[#9AA5B1]">{obs.created_at ? formatDateTime(obs.created_at) : ''}</span>
                                                </div>
                                                <p className="text-sm text-[#1F2933]">{obs.text}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* ===== INFORMES ===== */}
                {activeSection === 'informes' && (
                    <div className="space-y-3">
                        {reports.length === 0 ? (
                            <Card className="bg-white border-[#EAE8E3]"><CardContent className="py-8 text-center"><FileText className="h-10 w-10 mx-auto text-[#EAE8E3] mb-2" /><p className="text-sm text-[#9AA5B1]">Sin informes generados</p></CardContent></Card>
                        ) : reports.map(report => (
                            <Card key={report.id} className="bg-white border-[#EAE8E3] shadow-sm" data-testid={`report-card-${report.id}`}>
                                <CardContent className="py-4">
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-medium text-[#1F2933]">{MODULE_NAMES[report.module_id] || report.module_id}</p>
                                                <Badge className="text-[10px]" style={{
                                                    backgroundColor: report.status === 'borrador' ? 'rgba(232,178,122,0.1)' : 'rgba(95,175,157,0.1)',
                                                    color: report.status === 'borrador' ? '#E8B27A' : '#5FAF9D'
                                                }}>{report.status === 'borrador' ? 'Pendiente' : 'Aprobado'}</Badge>
                                                <span className="text-[10px] text-[#9AA5B1]">Cap. {report.chapter}</span>
                                            </div>
                                            <p className="text-xs text-[#9AA5B1]">Creado: {formatDate(report.created_at)}{report.approved_at && ` - Aprobado: ${formatDate(report.approved_at)}`}</p>
                                        </div>
                                        {report.status === 'borrador' && (
                                            <div className="flex gap-1.5 shrink-0">
                                                <Button size="sm" variant="outline" className="text-xs border-[#EAE8E3]" onClick={() => { setEditingReport(report.id); setEditedSummary(report.edited_content?.resumen || report.ai_content?.insight_usuario || ''); setEditedRecommendation(report.edited_content?.recomendacion || report.ai_content?.recomendacion_mentor || ''); }}>Editar</Button>
                                                <Button size="sm" className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white text-xs" onClick={() => handleApproveReport(report.id, false)} data-testid={`approve-report-${report.id}`}>Aprobar (interno)</Button>
                                                <Button size="sm" className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white text-xs" onClick={() => handleApproveReport(report.id, true)} data-testid={`approve-send-report-${report.id}`}>Aprobar y enviar al usuario</Button>
                                            </div>
                                        )}
                                    </div>
                                    {/* Content Preview */}
                                    {(report.edited_content || report.ai_content) && (
                                        <div className="p-3 bg-[#FBFBF9] rounded-lg text-xs text-[#616E7C]">
                                            <p>{report.edited_content?.resumen || report.ai_content?.insight_usuario || report.ai_content?.resumen || 'Sin detalle'}</p>
                                        </div>
                                    )}
                                    {/* Edit Dialog */}
                                    {editingReport === report.id && (
                                        <div className="mt-3 p-3 border border-[#3E5F6E]/20 rounded-lg bg-[#3E5F6E]/5 space-y-3">
                                            <div><Label className="text-xs text-[#425A65]">Resumen profesional</Label><Textarea value={editedSummary} onChange={e => setEditedSummary(e.target.value)} className="mt-1 border-[#EAE8E3] text-sm min-h-[60px]" /></div>
                                            <div><Label className="text-xs text-[#425A65]">Recomendación / Próximos pasos</Label><Textarea value={editedRecommendation} onChange={e => setEditedRecommendation(e.target.value)} className="mt-1 border-[#EAE8E3] text-sm min-h-[60px]" /></div>
                                            <div className="flex gap-2">
                                                <Button size="sm" className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white text-xs" onClick={() => handleEditReport(report.id)}>Guardar edición</Button>
                                                <Button size="sm" variant="outline" className="text-xs" onClick={() => setEditingReport(null)}>Cancelar</Button>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* ===== FEEDBACK ===== */}
                {activeSection === 'feedback' && (
                    <div className="space-y-3">
                        {feedbacks.length === 0 ? (
                            <Card className="bg-white border-[#EAE8E3]"><CardContent className="py-8 text-center"><Star className="h-10 w-10 mx-auto text-[#EAE8E3] mb-2" /><p className="text-sm text-[#9AA5B1]">Sin feedback de usuario</p></CardContent></Card>
                        ) : feedbacks.map((fb) => (
                            <Card key={`fb-${fb.module_id}-${fb.created_at}`} className="bg-white border-[#EAE8E3] shadow-sm" data-testid={`feedback-${fb.module_id}`}>
                                <CardContent className="py-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Badge className="bg-[#3E5F6E]/10 text-[#3E5F6E] text-xs">{MODULE_NAMES[fb.module_id] || fb.module_id}</Badge>
                                            <span className="text-[10px] text-[#9AA5B1]">Cap. {fb.chapter}</span>
                                        </div>
                                        <span className="text-[10px] text-[#9AA5B1]">{fb.created_at ? formatDate(fb.created_at) : ''}</span>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs">
                                        <div><span className="text-[#9AA5B1]">Calificación:</span> <span className="font-semibold text-[#E8B27A]">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span></div>
                                        {fb.difficulty && <div><span className="text-[#9AA5B1]">Dificultad:</span> <span className="font-medium text-[#1F2933]">{fb.difficulty}/5</span></div>}
                                        {fb.usefulness && <div><span className="text-[#9AA5B1]">Utilidad:</span> <span className="font-medium text-[#1F2933]">{fb.usefulness}/5</span></div>}
                                    </div>
                                    {fb.comment && <p className="text-sm text-[#616E7C] mt-2 italic">"{fb.comment}"</p>}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* ===== AUDITORÍA ===== */}
                {activeSection === 'auditoria' && (
                    <div className="space-y-2">
                        {auditLogs.length === 0 ? (
                            <Card className="bg-white border-[#EAE8E3]"><CardContent className="py-8 text-center"><Shield className="h-10 w-10 mx-auto text-[#EAE8E3] mb-2" /><p className="text-sm text-[#9AA5B1]">Sin registros de auditoría</p></CardContent></Card>
                        ) : auditLogs.map((log) => (
                            <div key={`${log.action}-${log.timestamp}`} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-[#EAE8E3] text-xs" data-testid={`audit-log-${log.action}`}>
                                <div className="w-6 h-6 bg-[#425A65]/10 rounded flex items-center justify-center shrink-0"><Shield className="h-3 w-3 text-[#425A65]" /></div>
                                <div className="flex-1 min-w-0">
                                    <span className="font-medium text-[#1F2933]">{log.action?.replace(/_/g, ' ')}</span>
                                    {log.details?.module_id && <span className="text-[#9AA5B1] ml-1">({MODULE_NAMES[log.details.module_id] || log.details.module_id})</span>}
                                </div>
                                <span className="text-[10px] text-[#9AA5B1] shrink-0">{log.timestamp ? formatDateTime(log.timestamp) : ''}</span>
                            </div>
                        ))}
                    </div>
                )}
            </main>
            <SENDAFooter />

            {/* Password Reset Dialog */}
            <Dialog open={resetPasswordOpen} onOpenChange={(open) => { if (!open) { setResetPasswordOpen(false); setTempPassword(''); } }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-[#1F2933] font-['Outfit']">
                            {tempPassword ? 'Contraseña reseteada' : 'Resetear contraseña'}
                        </DialogTitle>
                        <DialogDescription>
                            {tempPassword
                                ? 'La contraseña temporal se muestra a continuación. Cópiala y envíala manualmente al usuario.'
                                : `¿Resetear la contraseña de ${profile.name}? El progreso del usuario no se verá afectado.`
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {!tempPassword ? (
                        <div className="space-y-4">
                            <div className="p-3 bg-[#E8B27A]/10 border border-[#E8B27A]/20 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-[#E8B27A] mt-0.5 shrink-0" />
                                    <div className="text-xs text-[#616E7C]">
                                        <p className="font-medium text-[#E8B27A] mb-1">Importante</p>
                                        <ul className="space-y-1 list-disc pl-3">
                                            <li>Se generará una contraseña temporal</li>
                                            <li>El usuario deberá cambiarla en su próximo inicio de sesión</li>
                                            <li>El progreso, módulos y datos del usuario NO se modifican</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setResetPasswordOpen(false)} data-testid="cancel-reset-btn">Cancelar</Button>
                                <Button
                                    className="bg-[#A29DB0] hover:bg-[#8C87A0] text-white"
                                    onClick={handleResetPassword}
                                    disabled={resetLoading}
                                    data-testid="confirm-reset-btn"
                                >
                                    {resetLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reseteando...</> : <><KeyRound className="mr-2 h-4 w-4" />Resetear contraseña</>}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-4 bg-[#FBFBF9] rounded-lg border border-[#EAE8E3]">
                                <Label className="text-xs text-[#616E7C] mb-2 block">Contraseña temporal (mostrada una sola vez)</Label>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-white px-3 py-2 rounded-md border border-[#EAE8E3] text-sm font-mono text-[#1F2933] select-all" data-testid="temp-password-display">
                                        {tempPassword}
                                    </code>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopyPassword}
                                        className={copied ? 'border-[#5FAF9D] text-[#5FAF9D]' : 'border-[#EAE8E3] text-[#3E5F6E]'}
                                        data-testid="copy-password-btn"
                                    >
                                        {copied ? <><Check className="h-4 w-4 mr-1" />Copiada</> : <><Copy className="h-4 w-4 mr-1" />Copiar</>}
                                    </Button>
                                </div>
                            </div>
                            <div className="p-3 bg-[#5FAF9D]/5 border border-[#5FAF9D]/15 rounded-lg text-xs text-[#616E7C]">
                                <p><strong>Próximos pasos:</strong></p>
                                <ol className="list-decimal pl-4 mt-1 space-y-1">
                                    <li>Envía esta contraseña al usuario ({profile.email})</li>
                                    <li>El usuario inicia sesión con esta contraseña temporal</li>
                                    <li>El sistema le pedirá crear una contraseña nueva automáticamente</li>
                                </ol>
                            </div>
                            <div className="flex justify-end">
                                <Button onClick={() => { setResetPasswordOpen(false); setTempPassword(''); }} data-testid="close-reset-dialog-btn">Cerrar</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
